import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Box, Paper, Typography, Skeleton, IconButton, Tooltip } from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import DownloadIcon   from '@mui/icons-material/Download';
import DateRangeIcon  from '@mui/icons-material/DateRange';
import Beacon4BlendedKpiLineChart from '../charts/Beacon4BlendedKpiLineChart';
import Beacon4BlendedRagBadge     from './Beacon4BlendedRagBadge';
import Beacon4BlendedChartModal   from '../charts/Beacon4BlendedChartModal';
import celfinetLogo from '../../../../assets/Cyient_Slate_Std_RGB.png';
import { MONTH_LABELS, getYearSlice } from '../../utils/ragUtils';

export default function Beacon4BlendedKpiCard({ config, series, loading }) {
    const { viewYear } = useSelector(s => s.filters);
    const [modalOpen,  setModalOpen]  = useState(false);
    const [slicerOpen, setSlicerOpen] = useState(false);
    const chartRef = useRef(null);

    const data = series?.data ?? [];

    // Global year filter
    const [globalFrom, globalTo] = getYearSlice(data, viewYear);

    // Per-card slicer override
    const [sliceFrom, setSliceFrom] = useState(null);
    const [sliceTo,   setSliceTo]   = useState(null);
    React.useEffect(() => { setSliceFrom(null); setSliceTo(null); }, [viewYear, data.length]);

    const effectiveFrom = sliceFrom ?? globalFrom;
    const effectiveTo   = sliceTo   ?? globalTo;
    const allLabels     = data.map(d => `${MONTH_LABELS[d.month]} ${String(d.year).slice(2)}`);

    const handleExport = () => {
        const canvas = chartRef.current?.querySelector('canvas');
        if (!canvas) return;
        const link = document.createElement('a');
        link.download = `${config?.kpiLabel ?? 'chart'}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
    };

    return (
        <>
            <Paper elevation={0} sx={{
                border: '1px solid #e3e8ef', borderRadius: '12px',
                p: '12px 14px', bgcolor: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,.04)', mb: 1.25,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                           pointerEvents: 'none', zIndex: 0, width: '40%', display: 'flex', justifyContent: 'center', opacity: 0.05 }}>
                    <img src={celfinetLogo} alt="" style={{ width: '100%' }} />
                </Box>

                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                {config.kpiLabel}
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: '#8fa3bc' }}>{config.subtitle}</Typography>
                        </Box>
                        {!loading && series && (
                            <Box sx={{ display: 'flex', gap: 0.25 }}>
                                <Tooltip title="Per-card slicer" placement="top">
                                    <IconButton size="small" onClick={() => setSlicerOpen(v => !v)}
                                        sx={{ p: '3px', color: slicerOpen ? '#1565c0' : '#8fa3bc', '&:hover': { color: '#1565c0' } }}>
                                        <DateRangeIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Export PNG" placement="top">
                                    <IconButton size="small" onClick={handleExport}
                                        sx={{ p: '3px', color: '#8fa3bc', '&:hover': { color: '#1565c0' } }}>
                                        <DownloadIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Maximise" placement="top">
                                    <IconButton size="small" onClick={() => setModalOpen(true)}
                                        sx={{ p: '3px', color: '#8fa3bc', '&:hover': { color: '#1565c0' } }}>
                                        <OpenInFullIcon sx={{ fontSize: 13 }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                    </Box>

                    {slicerOpen && !loading && allLabels.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1, px: 0.5 }}>
                            <Typography sx={{ fontSize: 10, color: '#8fa3bc' }}>From:</Typography>
                            <select value={effectiveFrom} onChange={e => setSliceFrom(Number(e.target.value))}
                                style={{ fontSize: 11, padding: '2px 4px', border: '1px solid #cdd5e0', borderRadius: 4, color: '#1a2233', background: '#f7f8fa' }}>
                                {allLabels.map((l, i) => <option key={i} value={i} disabled={i >= effectiveTo}>{l}</option>)}
                            </select>
                            <Typography sx={{ fontSize: 10, color: '#8fa3bc' }}>To:</Typography>
                            <select value={effectiveTo} onChange={e => setSliceTo(Number(e.target.value))}
                                style={{ fontSize: 11, padding: '2px 4px', border: '1px solid #cdd5e0', borderRadius: 4, color: '#1a2233', background: '#f7f8fa' }}>
                                {allLabels.map((l, i) => <option key={i} value={i} disabled={i <= effectiveFrom}>{l}</option>)}
                            </select>
                            <Typography sx={{ fontSize: 10, color: '#8fa3bc' }}>({effectiveTo - effectiveFrom + 1}m)</Typography>
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                        <Box ref={chartRef} sx={{ flex: 1, minWidth: 0 }}>
                            {loading
                                ? <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 1 }} />
                                : <Beacon4BlendedKpiLineChart series={series} config={config} sliceFrom={effectiveFrom} sliceTo={effectiveTo} />
                            }
                        </Box>
                        <Box sx={{ flexShrink: 0 }}>
                            {loading
                                ? (<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1, minWidth: 115 }}>
                                    <Skeleton variant="rectangular" height={42} sx={{ borderRadius: 1 }} />
                                    <Skeleton variant="rectangular" height={42} sx={{ borderRadius: 1 }} />
                                  </Box>)
                                : <Beacon4BlendedRagBadge series={series} config={config} />
                            }
                        </Box>
                    </Box>
                </Box>
            </Paper>

            <Beacon4BlendedChartModal
                open={modalOpen} onClose={() => setModalOpen(false)}
                series={series} config={config}
                viewYear={viewYear}
            />
        </>
    );
}